import { useEffect, useRef, useState } from "react";
import { Link } from "@/components/i18n/LocaleLink";
import { useLocation } from "react-router-dom";
import {
  Home,
  Map as MapIcon,
  ChevronDown,
  Fish,
  Bug,
  Bird,
  Wheat,
  Flower2,
  PawPrint,
  ChefHat,
  Pickaxe,
  MapPin,
  Users,
  Store,
  CheckSquare,
  BookOpen,
  Newspaper,
  Globe,
  Trophy,
  Search,
  Menu,
  X,
  CloudSun,
  PartyPopper,
  Heart,
  ListChecks,
  Gift,
  Armchair,
  Shirt,
  DoorOpen,
} from "lucide-react";
import { ROUTES } from "@/lib/routing";
import { useLocale } from "@/lib/i18n/LocaleContext";
import { extractLocaleFromPath } from "@/lib/i18n/localize-path";
import { LOCALE_LABELS } from "@/lib/i18n/config";
import { LanguageModal } from "@/components/layout/LanguageModal";
import { SearchModal } from "@/components/search/SearchModal";

function usePrimaryNav() {
  const { t } = useLocale();
  return [
    { href: ROUTES.home, label: t("nav.home"), icon: Home },
    { href: "/map", label: t("nav.map"), icon: MapIcon },
    { href: "/tracker", label: t("nav.tracker"), icon: CheckSquare },
  ];
}

type ResourceGroup = "resourcesCollection" | "resourcesDecorate" | "resourcesExplore" | "resourcesLearn";

function useResourceGroups() {
  const { t } = useLocale();
  const groups: {
    group: ResourceGroup;
    items: { href: string; label: string; icon: typeof Fish; color: string; bg: string }[];
  }[] = [
    {
      group: "resourcesCollection",
      items: [
        { href: ROUTES.collection("fish"), label: t("nav.fish"), icon: Fish, color: "text-blue-600", bg: "bg-blue-50" },
        {
          href: ROUTES.collection("insect"),
          label: t("nav.insects"),
          icon: Bug,
          color: "text-amber-600",
          bg: "bg-amber-50",
        },
        { href: ROUTES.collection("bird"), label: t("nav.birds"), icon: Bird, color: "text-sky-600", bg: "bg-sky-50" },
        {
          href: ROUTES.collection("forageable"),
          label: t("nav.foraging"),
          icon: Pickaxe,
          color: "text-stone-600",
          bg: "bg-stone-50",
        },
        {
          href: ROUTES.collection("crop"),
          label: t("nav.crops"),
          icon: Wheat,
          color: "text-emerald-600",
          bg: "bg-emerald-50",
        },
        {
          href: ROUTES.collection("flower"),
          label: t("nav.flowers"),
          icon: Flower2,
          color: "text-rose-500",
          bg: "bg-rose-50",
        },
        {
          href: ROUTES.collection("animal"),
          label: t("nav.animals"),
          icon: PawPrint,
          color: "text-orange-600",
          bg: "bg-orange-50",
        },
        {
          href: ROUTES.collection("dish"),
          label: t("nav.recipes"),
          icon: ChefHat,
          color: "text-amber-700",
          bg: "bg-amber-50",
        },
      ],
    },
    {
      // The browse tables get their own group rather than joining "Collection": those are things
      // you catch one at a time and tick off, these are catalogues you shop through.
      group: "resourcesDecorate",
      items: [
        { href: "/furniture", label: t("nav.furniture"), icon: Armchair, color: "text-orange-700", bg: "bg-orange-50" },
        { href: "/wardrobe", label: t("nav.wardrobe"), icon: Shirt, color: "text-purple-600", bg: "bg-purple-50" },
        { href: "/buildings", label: t("nav.buildings"), icon: DoorOpen, color: "text-amber-800", bg: "bg-amber-50" },
      ],
    },
    {
      group: "resourcesExplore",
      items: [
        { href: "/locations", label: t("nav.locations"), icon: MapPin, color: "text-teal-600", bg: "bg-teal-50" },
        { href: "/npcs", label: t("nav.npcs"), icon: Users, color: "text-violet-600", bg: "bg-violet-50" },
        { href: "/shops", label: t("nav.shops"), icon: Store, color: "text-cyan-600", bg: "bg-cyan-50" },
        { href: "/events", label: t("nav.events"), icon: PartyPopper, color: "text-pink-600", bg: "bg-pink-50" },
        { href: "/pets", label: t("nav.pets"), icon: Heart, color: "text-rose-600", bg: "bg-rose-50" },
        {
          href: "/daily-checklist",
          label: t("nav.dailyChecklist"),
          icon: ListChecks,
          color: "text-lime-600",
          bg: "bg-lime-50",
        },
        { href: "/codes", label: t("nav.codes"), icon: Gift, color: "text-fuchsia-600", bg: "bg-fuchsia-50" },
      ],
    },
    {
      group: "resourcesLearn",
      items: [
        { href: "/guides", label: t("nav.guides"), icon: BookOpen, color: "text-emerald-600", bg: "bg-emerald-50" },
        { href: "/news", label: t("nav.news"), icon: Newspaper, color: "text-rose-600", bg: "bg-rose-50" },
        {
          href: "/achievements",
          label: t("nav.achievements"),
          icon: Trophy,
          color: "text-amber-600",
          bg: "bg-amber-50",
        },
        { href: "/weather", label: t("nav.weather"), icon: CloudSun, color: "text-sky-600", bg: "bg-sky-50" },
      ],
    },
  ];
  return groups;
}

function ResourcesMegaMenu() {
  const { t } = useLocale();
  const groups = useResourceGroups();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-bold text-muted-foreground transition-all hover:bg-secondary/70 hover:text-foreground"
      >
        {t("nav.resources")}
        <ChevronDown className={`size-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute left-0 z-50 mt-3 grid w-[42rem] grid-cols-3 gap-5 rounded-[1.75rem] border border-white/85 bg-white/95 p-5 shadow-2xl shadow-slate-900/10 backdrop-blur">
          {groups.map(({ group, items }) => (
            <div key={group} className="space-y-1">
              <p className="px-2 pb-1 text-[10px] font-black uppercase tracking-[0.2em] text-primary">
                {t(`nav.${group}`)}
              </p>
              {items.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-2.5 rounded-2xl px-2 py-2 transition-colors hover:bg-secondary/60"
                >
                  <div className={`flex size-8 shrink-0 items-center justify-center rounded-xl ${item.bg}`}>
                    <item.icon className={`size-4 ${item.color}`} />
                  </div>
                  <span className="text-sm font-bold text-foreground">{item.label}</span>
                </Link>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MobileMenu({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useLocale();
  const location = useLocation();
  const { path: pathWithoutLocale } = extractLocaleFromPath(location.pathname);
  const primaryNav = usePrimaryNav();
  const groups = useResourceGroups();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";
    const id = window.setTimeout(() => setMounted(true), 10);
    return () => {
      document.body.style.overflow = "";
      window.clearTimeout(id);
      setMounted(false);
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="lg:hidden">
      <div
        className={`fixed inset-0 top-16 z-40 bg-slate-900/40 backdrop-blur-[2px] transition-opacity duration-300 ${mounted ? "opacity-100" : "opacity-0"}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className={`fixed inset-x-0 top-16 z-50 max-h-[calc(100vh-4rem)] overflow-y-auto rounded-b-[2rem] border-b border-border/60 bg-background shadow-2xl transition-all duration-300 ease-out ${
          mounted ? "translate-y-0 opacity-100" : "-translate-y-4 opacity-0"
        }`}
      >
        <div className="space-y-6 p-5">
          <div className="grid grid-cols-3 gap-2">
            {primaryNav.map((link) => {
              const active = pathWithoutLocale === link.href;
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={onClose}
                  className={`flex flex-col items-center justify-center gap-1.5 rounded-2xl border px-2 py-3.5 text-center transition-all ${
                    active
                      ? "border-primary/30 bg-primary/10 text-primary"
                      : "border-border/50 bg-secondary/40 text-foreground hover:bg-secondary/70"
                  }`}
                >
                  <link.icon className="size-5" />
                  <span className="text-xs font-bold">{link.label}</span>
                </Link>
              );
            })}
          </div>

          {groups.map(({ group, items }) => (
            <div key={group}>
              <p className="mb-2 px-1 text-[10px] font-black uppercase tracking-[0.2em] text-primary">
                {t(`nav.${group}`)}
              </p>
              <div className="grid grid-cols-2 gap-2">
                {items.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={onClose}
                    className="flex items-center gap-2.5 rounded-2xl border border-border/40 bg-white/60 px-3 py-2.5 transition-all hover:border-border hover:bg-white active:scale-[0.98]"
                  >
                    <div className={`flex size-8 shrink-0 items-center justify-center rounded-xl ${item.bg}`}>
                      <item.icon className={`size-4 ${item.color}`} />
                    </div>
                    <span className="truncate text-sm font-bold text-foreground">{item.label}</span>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function Header() {
  const location = useLocation();
  const { path: pathWithoutLocale } = extractLocaleFromPath(location.pathname);
  const primaryNav = usePrimaryNav();
  const { t, locale } = useLocale();
  const [searchOpen, setSearchOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  // Read after mount, never during render. Node has no `navigator.platform`, so every
  // prerendered page ships "Ctrl K"; a Mac visitor's first client render produced "⌘K", the text
  // nodes diverged, and React threw #418 and discarded the whole server tree. The header is on
  // every page, so that was every route for every Mac visitor — measured in production, not
  // hypothetical (see CLAUDE.md's hydration section). Same deferral `usePersistentState` and
  // `CookieConsentBanner` already use: first client render matches the server exactly, the real
  // value lands one commit later.
  const [isMac, setIsMac] = useState(false);

  // navigator.platform is deprecated but still the only field every browser agrees on here;
  // userAgentData.platform is Chromium-only, and the fallback would be this anyway.
  useEffect(() => {
    setIsMac(/mac/i.test(navigator.platform));
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/60 bg-background/90 shadow-sm backdrop-blur-xl">
      <div className="relative mx-auto flex h-16 w-full max-w-[1400px] items-center gap-3 px-4 sm:px-6 lg:px-8">
        <Link to={ROUTES.home} className="group mr-4 flex shrink-0 items-center gap-2.5">
          <div className="relative flex size-9 items-center justify-center overflow-hidden rounded-2xl bg-primary/15 transition-transform group-hover:scale-105">
            <img
              src="/logo-64.png"
              alt="Heartopia"
              width={64}
              height={64}
              className="size-7 object-contain"
              loading="eager"
              decoding="async"
            />
          </div>
          <span className="hidden text-lg font-extrabold tracking-tight sm:inline">
            Heartopia<span className="text-primary">.guide</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 rounded-full border border-border/50 bg-white/45 p-1 lg:flex">
          {primaryNav.map((link) => {
            const active = pathWithoutLocale === link.href;
            return (
              <Link
                key={link.href}
                to={link.href}
                className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-bold transition-all ${
                  active
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:bg-secondary/70 hover:text-foreground"
                }`}
              >
                <link.icon className="size-4" />
                {link.label}
              </Link>
            );
          })}
          <div className="mx-1 h-6 w-px bg-border" />
          <ResourcesMegaMenu />
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            className="flex h-10 w-full max-w-xs items-center gap-2 rounded-full border border-border/60 bg-white/60 px-3.5 text-sm font-semibold text-muted-foreground transition-all hover:bg-white"
          >
            <Search className="size-4" />
            <span className="hidden truncate sm:inline">{t("search.placeholder")}</span>
            <kbd className="ml-auto hidden shrink-0 rounded-md border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground md:inline-flex">
              {isMac ? "⌘K" : "Ctrl K"}
            </kbd>
          </button>

          <button
            type="button"
            onClick={() => setLanguageOpen(true)}
            aria-label={t("language.title")}
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-sm font-bold text-muted-foreground transition-all hover:bg-secondary/70 hover:text-foreground"
          >
            <Globe className="size-4" />
            <span className="hidden sm:inline">{LOCALE_LABELS[locale]}</span>
          </button>

          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={t("nav.menu")}
            className="inline-flex size-9 items-center justify-center rounded-full text-muted-foreground transition-all hover:bg-secondary/70 hover:text-foreground lg:hidden"
          >
            {mobileOpen ? <X className="size-5" /> : <Menu className="size-5" />}
          </button>
        </div>

        <MobileMenu open={mobileOpen} onClose={() => setMobileOpen(false)} />
      </div>

      <SearchModal open={searchOpen} onOpenChange={setSearchOpen} />
      <LanguageModal open={languageOpen} onOpenChange={setLanguageOpen} />
    </header>
  );
}
